import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  runAnalyzer, getAnalyzerProgress, getAnalyzerResult, getTickersList,
} from '../api';
import TickerDetail from './TickerDetail';

// ── Theme ─────────────────────────────────────────────────────────────────────
const C = {
  pageBg:    '#F1F5F9',
  cardBg:    '#FFFFFF',
  border:    '#E2E8F0',
  borderMd:  '#CBD5E1',
  text:      '#0F172A',
  textMd:    '#475569',
  textSm:    '#94A3B8',
  primary:   '#2563EB',
  primaryLt: '#EFF6FF',
  success:   '#16A34A',
  successLt: '#DCFCE7',
  danger:    '#DC2626',
  dangerLt:  '#FEE2E2',
  warning:   '#D97706',
  shadow:    '0 1px 3px rgba(0,0,0,0.08)',
  shadowMd:  '0 4px 12px rgba(0,0,0,0.10)',
};

const S = {
  page:     { background: C.pageBg, minHeight: '100vh', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", color: C.text },
  wrap:     { maxWidth: 1400, margin: '0 auto', padding: '24px 28px' },
  card:     { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: C.shadow, marginBottom: 16 },
  cardBody: { padding: '16px 20px' },
  row:      { display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' },
  label:    { display: 'block', fontSize: 12, fontWeight: 500, color: C.textMd, marginBottom: 5 },
  input:    {
    padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 7,
    fontSize: 13, color: C.text, background: C.cardBg, boxSizing: 'border-box', outline: 'none', width: '100%',
  },
  select:   {
    padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 7,
    fontSize: 13, color: C.text, background: C.cardBg, boxSizing: 'border-box', cursor: 'pointer', width: '100%',
  },
  btnPrimary: {
    padding: '9px 24px', background: C.primary, color: '#fff',
    border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 14, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  btnSecondary: {
    padding: '7px 14px', background: 'transparent', color: C.primary,
    border: `1px solid ${C.primary}`, borderRadius: 7, fontWeight: 500, fontSize: 13, cursor: 'pointer',
  },
  btnSm: {
    padding: '5px 10px', background: 'transparent', color: C.textMd,
    border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, cursor: 'pointer',
  },
  sectionTitle: {
    fontSize: 11, fontWeight: 700, color: C.textSm, textTransform: 'uppercase',
    letterSpacing: '0.06em', marginBottom: 14,
  },
  metCard: {
    background: C.pageBg, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: '10px 14px', minWidth: 110,
  },
  metLabel: { fontSize: 10, color: C.textSm, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 },
  metValue: { fontSize: 18, fontWeight: 700, color: C.text },
  th: {
    padding: '8px 12px', background: '#F8FAFC', border: `1px solid ${C.border}`,
    color: C.textMd, fontWeight: 600, fontSize: 12, textAlign: 'left', whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 2,
  },
  td:    { padding: '7px 12px', border: `1px solid ${C.border}`, fontSize: 12, color: C.text, whiteSpace: 'nowrap' },
  badge: (clr) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    background: clr === 'green' ? C.successLt : clr === 'red' ? C.dangerLt : '#F1F5F9',
    color:      clr === 'green' ? C.success   : clr === 'red' ? C.danger   : C.textMd,
  }),
};

// ── T-window presets ───────────────────────────────────────────────────────────
const T_BUY_OPTIONS  = [0.25, 0.5, 1, 2, 3, 5, 7, 14, 30];
const T_SELL_OPTIONS = [1, 2, 3, 5, 7, 14, 30];

function fmtPct(v)   { if (v == null) return '—'; return (v >= 0 ? '+' : '') + Number(v).toFixed(2) + '%'; }
function fmtUsd(v)   { if (v == null) return '—'; return '$' + Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }); }
function fmtDur(bars, dataType) {
  if (!bars) return '—';
  const bpd = dataType === 'daily' ? 1 : dataType === '5m' ? 78 : dataType === '1m' ? 390 : 6.5;
  const days = bars / bpd;
  if (days < 1) return `${bars}б (${Math.round(days * 24)}г)`;
  if (days < 7)  return `${bars}б (${days.toFixed(1)}д)`;
  return `${bars}б (${Math.round(days / 5)}тиж)`;
}
function fmtTime(s) {
  if (!s) return '—';
  return s.slice(0, 16).replace('T', ' ');
}
function reasonLabel(r) {
  switch (r) {
    case 'drop_in_period':       return 'Падіння';
    case 'hard_stop_loss':       return 'Hard stop';
    case 'no_growth':            return 'Немає росту';
    case 'stagnation':           return 'Стагнація';
    case 'overnight_protection': return 'Ніч/вих';
    case 'end_of_period':        return 'Кінець';
    default:                     return r || '—';
  }
}

// ── Defaults ──────────────────────────────────────────────────────────────────
const DEFAULT_PARAMS = {
  from:        '2025-01-01',
  to:          '2026-02-28',
  data_type:   'hourly',
  buy_pct:     0.5,
  t_buy:       1,
  drop_pct:    1.0,
  t_sell:      3,
  capital:     10000,
  reinvest:    true,
  hard_stop:   '',
  stagnation:  { enabled: false, min: -0.5, max: 0.5, bars: 30 },
  risk_prot:   { sell_overnight: false, sell_weekend: false },
};

const PRESETS_KEY = 'single_backtest_presets';

function loadPresets() {
  try { return JSON.parse(localStorage.getItem(PRESETS_KEY) || '[]'); }
  catch { return []; }
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function SingleTickerBacktest({ initialTicker = '' }) {
  // ── Ticker picker ────────────────────────────────────────────────────────────
  const [ticker,           setTicker]           = useState(initialTicker);
  const [tickerSearch,     setTickerSearch]     = useState('');
  const [tickersList,      setTickersList]      = useState([]);
  const [showDropdown,     setShowDropdown]     = useState(false);
  const dropdownRef = useRef(null);

  // ── Params ───────────────────────────────────────────────────────────────────
  const [params, setParams]               = useState(DEFAULT_PARAMS);
  const [advancedOpen, setAdvancedOpen]   = useState(false);

  // ── Presets ──────────────────────────────────────────────────────────────────
  const [savedPresets, setSavedPresets]   = useState(loadPresets);
  const [presetName,   setPresetName]     = useState('');

  // ── Run state ────────────────────────────────────────────────────────────────
  const [status,     setStatus]     = useState('idle');   // idle|loading|done|error
  const [progress,   setProgress]   = useState(0);
  const [result,     setResult]     = useState(null);
  const [errorMsg,   setErrorMsg]   = useState('');
  const pollRef = useRef(null);

  // ── Chart view ───────────────────────────────────────────────────────────────
  const [showChart, setShowChart]   = useState(false);

  // ── Load tickers list ────────────────────────────────────────────────────────
  useEffect(() => {
    getTickersList()
      .then(r => setTickersList(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  // ── Read ?ticker from URL on first mount ─────────────────────────────────────
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('ticker');
    if (q) setTicker(q.toUpperCase());
  }, []);

  // ── Update ticker when initialTicker prop changes ────────────────────────────
  useEffect(() => {
    if (initialTicker) setTicker(initialTicker);
  }, [initialTicker]);

  // ── Close dropdown on outside click ──────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Cleanup poll on unmount ───────────────────────────────────────────────────
  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  // ── Filtered ticker dropdown ─────────────────────────────────────────────────
  const filteredTickers = useMemo(() => {
    const q = tickerSearch.toLowerCase().trim();
    if (!q) return tickersList.slice(0, 20);
    return tickersList
      .filter(t =>
        t.ticker.toLowerCase().includes(q) ||
        t.name.toLowerCase().includes(q) ||
        (t.sector || '').toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [tickersList, tickerSearch]);

  // ── Param helpers ─────────────────────────────────────────────────────────────
  const setP = useCallback((key, val) => setParams(p => ({ ...p, [key]: val })), []);
  const setStag = useCallback((key, val) => setParams(p => ({
    ...p, stagnation: { ...p.stagnation, [key]: val }
  })), []);
  const setRisk = useCallback((key, val) => setParams(p => ({
    ...p, risk_prot: { ...p.risk_prot, [key]: val }
  })), []);

  // ── Run backtest ──────────────────────────────────────────────────────────────
  const handleRun = useCallback(async () => {
    if (!ticker.trim()) { alert('Оберіть тікер!'); return; }
    if (pollRef.current) clearTimeout(pollRef.current);

    setStatus('loading');
    setProgress(0);
    setResult(null);
    setShowChart(false);
    setErrorMsg('');

    const body = {
      name:              `Single: ${ticker}`,
      start_time:        params.from,
      stop_time:         params.to,
      data_type:         params.data_type,
      x_buy:             Number(params.buy_pct),
      t_hours_buy:       Number(params.t_buy),
      y_sell:            Number(params.drop_pct),
      sell_window_days:  Number(params.t_sell),
      sell_mode:         'rolling',
      num_companies:     [1],
      pos_size:          100,
      initial_capital:   Number(params.capital),
      reinvest:          params.reinvest,
      specific_tickers:  [ticker.trim()],
      stagnation_enabled: params.stagnation.enabled,
      stagnation_min_pct: Number(params.stagnation.min),
      stagnation_max_pct: Number(params.stagnation.max),
      stagnation_bars:    Number(params.stagnation.bars),
      risk_protection: {
        enabled:       !!(params.hard_stop || params.risk_prot.sell_overnight || params.risk_prot.sell_weekend),
        sell_overnight: params.risk_prot.sell_overnight,
        sell_weekend:   params.risk_prot.sell_weekend,
        max_loss_pct:   params.hard_stop !== '' ? Number(params.hard_stop) : null,
      },
    };

    let resultId, batchId;
    try {
      const resp = await runAnalyzer(body);
      resultId = resp.data.id;
      batchId  = resp.data.batch_id;
    } catch (e) {
      setStatus('error');
      setErrorMsg(e.response?.data?.detail || e.message || 'Не вдалося запустити');
      return;
    }

    const poll = async () => {
      try {
        const prog = await getAnalyzerProgress(batchId);
        const { status: st, progress: pct } = prog.data;
        setProgress(pct || 0);
        if (st === 'completed') {
          const r = await getAnalyzerResult(resultId);
          setResult(r.data);
          setStatus('done');
          setShowChart(true);
        } else if (st === 'failed') {
          const r = await getAnalyzerResult(resultId).catch(() => ({ data: {} }));
          setStatus('error');
          setErrorMsg(r.data?.error_detail || r.data?.metrics?.error || 'Помилка симуляції');
        } else {
          pollRef.current = setTimeout(poll, 600);
        }
      } catch (e) {
        pollRef.current = setTimeout(poll, 1000);
      }
    };
    poll();
  }, [ticker, params]);

  // ── Presets ───────────────────────────────────────────────────────────────────
  const handleSavePreset = useCallback(() => {
    if (!presetName.trim()) return;
    const updated = [
      { id: Date.now(), name: presetName.trim(), params: { ...params }, ticker },
      ...savedPresets.filter(p => p.name !== presetName.trim()),
    ].slice(0, 20);
    localStorage.setItem(PRESETS_KEY, JSON.stringify(updated));
    setSavedPresets(updated);
    setPresetName('');
  }, [presetName, params, ticker, savedPresets]);

  const handleLoadPreset = useCallback((preset) => {
    setParams(preset.params);
    if (preset.ticker) setTicker(preset.ticker);
  }, []);

  // ── Ticker obj for TickerDetail ───────────────────────────────────────────────
  const tickerInfo = useMemo(() => {
    const info = tickersList.find(t => t.ticker === ticker);
    return {
      ticker: ticker,
      name:   info?.name || ticker,
      sector: info?.sector || '',
      daily:  { has_data: true },
      hourly: { has_data: params.data_type === 'hourly' },
    };
  }, [ticker, tickersList, params.data_type]);

  // ── Trade context for TickerDetail ────────────────────────────────────────────
  const tradeContext = useMemo(() => {
    if (!result?.trades?.length) return null;
    const allTrades = result.trades;
    const times     = allTrades.map(t => new Date(t.time)).filter(d => !isNaN(d));
    const minTime   = new Date(Math.min(...times));
    const maxTime   = new Date(Math.max(...times));
    minTime.setDate(minTime.getDate() - 5);
    maxTime.setDate(maxTime.getDate() + 5);
    return {
      dataType:        params.data_type,
      fromDate:        minTime.toISOString().split('T')[0],
      toDate:          maxTime.toISOString().split('T')[0],
      allTickerTrades: allTrades,
      buyTrade:        allTrades.find(t => t.action === 'BUY') || null,
      sellTrade:       null,
    };
  }, [result, params.data_type]);

  // ── Metrics ───────────────────────────────────────────────────────────────────
  const metrics = result?.metrics || {};
  const trades  = result?.trades  || [];

  const closedTrades = useMemo(() => {
    // Pair BUY + SELL
    const pairs = [];
    let pendingBuy = null;
    let idx = 1;
    [...trades].sort((a, b) => new Date(a.time) - new Date(b.time)).forEach(t => {
      if (t.action === 'BUY') {
        pendingBuy = { ...t, idx };
        idx++;
      } else if (t.action === 'SELL' && pendingBuy) {
        pairs.push({ buy: pendingBuy, sell: t });
        pendingBuy = null;
      }
    });
    if (pendingBuy) pairs.push({ buy: pendingBuy, sell: null });
    return pairs;
  }, [trades]);

  const metricRows = [
    { label: 'Return',    value: fmtPct(metrics.total_return),   color: metrics.total_return >= 0 ? C.success : C.danger },
    { label: 'Yearly',    value: fmtPct(metrics.yearly_return),  color: metrics.yearly_return >= 0 ? C.success : C.danger },
    { label: 'Monthly',   value: fmtPct(metrics.monthly_return), color: metrics.monthly_return >= 0 ? C.success : C.danger },
    { label: 'Win Rate',  value: metrics.win_rate != null ? `${Number(metrics.win_rate).toFixed(1)}%` : '—', color: metrics.win_rate >= 60 ? C.success : metrics.win_rate >= 40 ? C.warning : C.danger },
    { label: 'Sharpe',    value: metrics.sharpe_ratio != null ? Number(metrics.sharpe_ratio).toFixed(3) : '—', color: metrics.sharpe_ratio >= 1 ? C.success : C.text },
    { label: 'Max DD',    value: metrics.max_drawdown != null ? `-${Number(metrics.max_drawdown).toFixed(2)}%` : '—', color: metrics.max_drawdown <= 10 ? C.success : metrics.max_drawdown <= 20 ? C.warning : C.danger },
    { label: 'Trades',    value: metrics.num_trades ?? '—', color: C.text },
    { label: 'Capital',   value: fmtUsd(metrics.starting_capital), color: C.textMd },
    { label: 'Final',     value: fmtUsd(metrics.final_balance),    color: metrics.final_balance >= metrics.starting_capital ? C.success : C.danger },
    { label: 'Commission',value: fmtUsd(metrics.total_commission), color: C.textSm },
  ];

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.wrap}>

        {/* ── Page title ────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0 }}>
            ⚡ Single Ticker Backtest
          </h1>
          <p style={{ fontSize: 13, color: C.textSm, marginTop: 4 }}>
            Швидкий бектест однієї акції з повним графіком та угодами
          </p>
        </div>

        {/* ── CARD 1: Ticker + Dates + Data type ──────────────────────── */}
        <div style={S.card}>
          <div style={S.cardBody}>
            <div style={S.sectionTitle}>Тікер і часовий проміжок</div>
            <div style={{ ...S.row, alignItems: 'flex-end', gap: 20 }}>

              {/* Ticker picker */}
              <div style={{ flex: '0 0 220px' }}>
                <label style={S.label}>Тікер</label>
                <div style={{ position: 'relative' }} ref={dropdownRef}>
                  <input
                    style={{ ...S.input, fontWeight: 700, fontFamily: 'monospace', fontSize: 14 }}
                    placeholder="напр. NVDA"
                    value={tickerSearch || (showDropdown ? '' : ticker)}
                    onChange={e => { setTickerSearch(e.target.value); setShowDropdown(true); }}
                    onFocus={() => { setTickerSearch(''); setShowDropdown(true); }}
                  />
                  {ticker && !showDropdown && (
                    <div style={{
                      position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                      fontSize: 11, color: C.success, fontWeight: 700, pointerEvents: 'none',
                    }}>✓</div>
                  )}
                  {showDropdown && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                      background: C.cardBg, border: `1px solid ${C.borderMd}`,
                      borderRadius: 8, boxShadow: C.shadowMd, maxHeight: 260, overflowY: 'auto', marginTop: 2,
                    }}>
                      {filteredTickers.length === 0 ? (
                        <div style={{ padding: '10px 14px', color: C.textSm, fontSize: 12 }}>Нічого не знайдено</div>
                      ) : filteredTickers.map(t => (
                        <div
                          key={t.ticker}
                          style={{
                            padding: '8px 14px', cursor: 'pointer',
                            background: t.ticker === ticker ? C.primaryLt : 'transparent',
                            borderBottom: `1px solid ${C.border}20`,
                          }}
                          onMouseDown={e => { e.preventDefault(); setTicker(t.ticker); setTickerSearch(''); setShowDropdown(false); }}
                        >
                          <span style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 13 }}>{t.ticker}</span>
                          {t.name && <span style={{ fontSize: 11, color: C.textSm, marginLeft: 8 }}>{t.name}</span>}
                          {t.sector && <span style={{ fontSize: 11, color: C.primary, marginLeft: 6 }}>{t.sector}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* From date */}
              <div style={{ flex: '0 0 140px' }}>
                <label style={S.label}>Від</label>
                <input type="date" style={S.input} value={params.from}
                  onChange={e => setP('from', e.target.value)} />
              </div>

              {/* To date */}
              <div style={{ flex: '0 0 140px' }}>
                <label style={S.label}>До</label>
                <input type="date" style={S.input} value={params.to}
                  onChange={e => setP('to', e.target.value)} />
              </div>

              {/* Data type */}
              <div style={{ flex: '0 0 130px' }}>
                <label style={S.label}>Дані</label>
                <select style={S.select} value={params.data_type}
                  onChange={e => setP('data_type', e.target.value)}>
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="5m">5-min</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ── CARD 2: Strategy params ──────────────────────────────────── */}
        <div style={S.card}>
          <div style={S.cardBody}>
            <div style={S.sectionTitle}>Параметри стратегії</div>
            <div style={{ ...S.row, alignItems: 'flex-end', gap: 20 }}>

              {/* Buy % */}
              <div style={{ flex: '0 0 110px' }}>
                <label style={S.label}>Buy% (зростання)</label>
                <input type="number" step="0.1" min="0.1" style={S.input}
                  value={params.buy_pct}
                  onChange={e => setP('buy_pct', e.target.value)} />
              </div>

              {/* T-Buy */}
              <div style={{ flex: '0 0 110px' }}>
                <label style={S.label}>T-Buy (торг. днів)</label>
                <select style={S.select} value={params.t_buy}
                  onChange={e => setP('t_buy', Number(e.target.value))}>
                  {T_BUY_OPTIONS.map(v => (
                    <option key={v} value={v}>
                      {v < 1 ? `${v * 24}г` : v === 1 ? '1 день' : v < 7 ? `${v} дні` : `${v} днів`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Drop % */}
              <div style={{ flex: '0 0 110px' }}>
                <label style={S.label}>Drop% (продаж)</label>
                <input type="number" step="0.1" min="0.1" style={S.input}
                  value={params.drop_pct}
                  onChange={e => setP('drop_pct', e.target.value)} />
              </div>

              {/* T-Sell */}
              <div style={{ flex: '0 0 110px' }}>
                <label style={S.label}>T-Sell (торг. днів)</label>
                <select style={S.select} value={params.t_sell}
                  onChange={e => setP('t_sell', Number(e.target.value))}>
                  {T_SELL_OPTIONS.map(v => (
                    <option key={v} value={v}>
                      {v === 1 ? '1 день' : v < 7 ? `${v} дні` : `${v} днів`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Capital */}
              <div style={{ flex: '0 0 120px' }}>
                <label style={S.label}>Капітал ($)</label>
                <input type="number" step="1000" min="100" style={S.input}
                  value={params.capital}
                  onChange={e => setP('capital', e.target.value)} />
              </div>

              {/* Reinvest */}
              <div style={{ flex: '0 0 auto', paddingBottom: 2 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: C.text }}>
                  <input type="checkbox" checked={params.reinvest}
                    onChange={e => setP('reinvest', e.target.checked)}
                    style={{ width: 14, height: 14, accentColor: C.primary }} />
                  Реінвест
                </label>
                <div style={{ fontSize: 10, color: C.textSm, marginTop: 3 }}>
                  {params.reinvest ? '↗ складний %' : '→ фіксована сума'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── CARD 3: Advanced (collapsed) ────────────────────────────── */}
        <div style={S.card}>
          <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setAdvancedOpen(o => !o)}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.textMd, flex: 1 }}>
              {advancedOpen ? '▾' : '▸'} Advanced (Stagnation · Hard Stop · Risk Protection)
            </span>
          </div>
          {advancedOpen && (
            <div style={{ ...S.cardBody, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }}>

                {/* Stagnation */}
                <div>
                  <div style={{ ...S.sectionTitle, marginBottom: 10 }}>Stagnation Sell</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    <input type="checkbox" checked={params.stagnation.enabled}
                      onChange={e => setStag('enabled', e.target.checked)}
                      style={{ width: 14, height: 14, accentColor: C.primary }} />
                    Увімкнути
                  </label>
                  {params.stagnation.enabled && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div>
                        <label style={S.label}>Min %</label>
                        <input type="number" step="0.1" style={S.input}
                          value={params.stagnation.min}
                          onChange={e => setStag('min', parseFloat(e.target.value) || 0)} />
                      </div>
                      <div>
                        <label style={S.label}>Max %</label>
                        <input type="number" step="0.1" style={S.input}
                          value={params.stagnation.max}
                          onChange={e => setStag('max', parseFloat(e.target.value) || 0)} />
                      </div>
                      <div>
                        <label style={S.label}>Bars</label>
                        <input type="number" step="1" min="1" style={S.input}
                          value={params.stagnation.bars}
                          onChange={e => setStag('bars', parseInt(e.target.value) || 1)} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Hard stop */}
                <div>
                  <div style={{ ...S.sectionTitle, marginBottom: 10 }}>Hard Stop Loss</div>
                  <label style={S.label}>Макс. збиток (%) — порожньо = вимкнено</label>
                  <input type="number" step="0.5" min="0.5" max="50" style={S.input}
                    placeholder="напр. 15"
                    value={params.hard_stop}
                    onChange={e => setP('hard_stop', e.target.value)} />
                  {params.hard_stop && (
                    <div style={{ fontSize: 11, color: C.textSm, marginTop: 4 }}>
                      Продати якщо поточний збиток ≥ {params.hard_stop}%
                    </div>
                  )}
                </div>

                {/* Risk protection */}
                <div>
                  <div style={{ ...S.sectionTitle, marginBottom: 10 }}>Risk Protection</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10, cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={params.risk_prot.sell_overnight}
                      onChange={e => setRisk('sell_overnight', e.target.checked)}
                      style={{ width: 14, height: 14, accentColor: C.primary }} />
                    Продавати перед ніччю
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={params.risk_prot.sell_weekend}
                      onChange={e => setRisk('sell_weekend', e.target.checked)}
                      style={{ width: 14, height: 14, accentColor: C.primary }} />
                    Продавати перед вихідними
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── CARD 4: Actions + Presets ────────────────────────────────── */}
        <div style={S.card}>
          <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>

            {/* Run button */}
            <button
              style={{ ...S.btnPrimary, opacity: status === 'loading' ? 0.7 : 1 }}
              onClick={handleRun}
              disabled={status === 'loading'}
            >
              {status === 'loading' ? (
                <>
                  <span style={{
                    display: 'inline-block', width: 14, height: 14, borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff',
                    animation: 'spin 0.7s linear infinite',
                  }} />
                  {progress > 0 ? `${progress}%` : 'Завантаження...'}
                </>
              ) : (
                <>▶ Запустити</>
              )}
            </button>

            <div style={{ width: 1, height: 28, background: C.border }} />

            {/* Save preset */}
            <input
              style={{ ...S.input, width: 160 }}
              placeholder="Назва пресету..."
              value={presetName}
              onChange={e => setPresetName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSavePreset(); }}
            />
            <button style={S.btnSecondary} onClick={handleSavePreset}>
              💾 Зберегти
            </button>

            {/* Load preset */}
            {savedPresets.length > 0 && (
              <select
                style={{ ...S.select, width: 180 }}
                value=""
                onChange={e => {
                  const p = savedPresets.find(x => x.id === +e.target.value);
                  if (p) handleLoadPreset(p);
                }}
              >
                <option value="">Завантажити пресет...</option>
                {savedPresets.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.ticker ? `[${p.ticker}] ` : ''}{p.name}
                  </option>
                ))}
              </select>
            )}

            {/* Clear presets */}
            {savedPresets.length > 0 && (
              <button
                style={{ ...S.btnSm, color: C.danger }}
                onClick={() => {
                  if (window.confirm('Видалити всі пресети?')) {
                    localStorage.removeItem(PRESETS_KEY);
                    setSavedPresets([]);
                  }
                }}
              >
                🗑 Очистити
              </button>
            )}
          </div>
        </div>

        {/* ── Spinner CSS ───────────────────────────────────────────────── */}
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        {/* ── Error ─────────────────────────────────────────────────────── */}
        {status === 'error' && (
          <div style={{
            background: C.dangerLt, border: `1px solid ${C.danger}`, borderRadius: 10,
            padding: '14px 20px', marginBottom: 16, color: C.danger, fontSize: 13, fontWeight: 500,
          }}>
            ❌ {errorMsg || 'Помилка симуляції'}
          </div>
        )}

        {/* ── Results ───────────────────────────────────────────────────── */}
        {status === 'done' && result && (
          <>
            {/* Metrics + Chart */}
            <div style={{ display: 'grid', gridTemplateColumns: '168px 1fr', gap: 16, marginBottom: 16 }}>

              {/* Metrics column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {metricRows.map(m => (
                  <div key={m.label} style={S.metCard}>
                    <div style={S.metLabel}>{m.label}</div>
                    <div style={{ ...S.metValue, color: m.color }}>{m.value}</div>
                  </div>
                ))}

                {/* Toggle chart button */}
                <button
                  style={{ ...S.btnSecondary, marginTop: 4, justifyContent: 'center' }}
                  onClick={() => setShowChart(v => !v)}
                >
                  {showChart ? '▲ Приховати' : '▼ Графік'}
                </button>
              </div>

              {/* Chart */}
              {showChart && tradeContext && ticker && (
                <div style={{
                  border: `1px solid ${C.border}`, borderRadius: 12,
                  overflow: 'hidden', background: C.cardBg,
                }}>
                  <TickerDetail
                    ticker={tickerInfo}
                    onBack={null}
                    tradeContext={tradeContext}
                  />
                </div>
              )}
            </div>

            {/* ── Trades table ───────────────────────────────────────────── */}
            <div style={{ ...S.card, marginBottom: 0 }}>
              <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>
                  Угоди — {closedTrades.length} пар
                  {closedTrades.filter(p => !p.sell).length > 0 && (
                    <span style={{ color: C.warning, marginLeft: 8, fontSize: 12 }}>
                      ({closedTrades.filter(p => !p.sell).length} відкритих)
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 12, color: C.textSm }}>
                  {closedTrades.filter(p => p.sell && p.sell.pnl_pct > 0).length} прибуткових /
                  {closedTrades.filter(p => p.sell && p.sell.pnl_pct != null && p.sell.pnl_pct <= 0).length} збиткових
                </span>
              </div>
              <div style={{ overflowX: 'auto', maxHeight: 480 }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      {['#', 'Час купівлі', 'Ціна купівлі', 'Час продажу', 'Ціна продажу',
                        'P&L %', 'P&L $', 'Тривалість', 'Причина продажу'].map(h => (
                        <th key={h} style={S.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {closedTrades.map((pair, i) => {
                      const pnl = pair.sell?.pnl_pct;
                      const clr = pnl == null ? null : pnl > 0 ? 'green' : pnl < 0 ? 'red' : null;
                      return (
                        <tr key={i} style={{ background: i % 2 ? '#FAFAFA' : C.cardBg }}>
                          <td style={{ ...S.td, color: C.textSm, fontWeight: 600 }}>{i + 1}</td>
                          <td style={S.td}>{fmtTime(pair.buy.time)}</td>
                          <td style={{ ...S.td, fontFamily: 'monospace' }}>
                            ${Number(pair.buy.price).toFixed(2)}
                          </td>
                          <td style={S.td}>
                            {pair.sell ? fmtTime(pair.sell.time) : <span style={{ color: C.warning }}>відкрита</span>}
                          </td>
                          <td style={{ ...S.td, fontFamily: 'monospace' }}>
                            {pair.sell ? `$${Number(pair.sell.price).toFixed(2)}` : '—'}
                          </td>
                          <td style={{ ...S.td, textAlign: 'right' }}>
                            {pair.sell && pnl != null ? (
                              <span style={S.badge(clr)}>{fmtPct(pnl)}</span>
                            ) : '—'}
                          </td>
                          <td style={{ ...S.td, textAlign: 'right', fontFamily: 'monospace' }}>
                            {pair.sell?.change_usd != null
                              ? <span style={{ color: pair.sell.change_usd >= 0 ? C.success : C.danger, fontWeight: 600 }}>
                                  {pair.sell.change_usd >= 0 ? '+' : ''}{fmtUsd(pair.sell.change_usd)}
                                </span>
                              : '—'}
                          </td>
                          <td style={{ ...S.td, color: C.textSm }}>
                            {pair.sell?.duration_bars ? fmtDur(pair.sell.duration_bars, params.data_type) : '—'}
                          </td>
                          <td style={S.td}>
                            {pair.sell
                              ? <span style={{ fontSize: 11, color: C.textMd }}>{reasonLabel(pair.sell.description)}</span>
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                    {closedTrades.length === 0 && (
                      <tr>
                        <td colSpan={9} style={{ ...S.td, textAlign: 'center', color: C.textSm, padding: 24 }}>
                          Угод не знайдено
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
